import { foregroundLoading } from "@/lib/foregroundLoading";
import { isForegroundFetching, queryOperationName, type QueryLike } from "@shared/queryForegroundPolicy";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useLocation } from "wouter";

const tokensByHash = new Map<string, number>();
let trackedGeneration = -1;

function syncQuery(query: QueryLike) {
  const hash = String(query.queryHash ?? JSON.stringify(query.queryKey));
  const fetching = isForegroundFetching(query);
  const existing = tokensByHash.get(hash);
  if (fetching && existing == null) {
    tokensByHash.set(hash, foregroundLoading.beginWork("query", queryOperationName(query)));
  } else if (!fetching && existing != null) {
    foregroundLoading.end(existing);
    tokensByHash.delete(hash);
  }
}

export function queryForegroundTokenCount() {
  return tokensByHash.size;
}

export function useGlobalForegroundLoading() {
  const queryClient = useQueryClient();
  const [location] = useLocation();
  const navToken = useRef<number | null>(null);

  useEffect(() => {
    const cache = queryClient.getQueryCache();
    const dropStaleQueryTokens = () => {
      const gen = foregroundLoading.getSnapshot().generation;
      if (gen === trackedGeneration) return;
      trackedGeneration = gen;
      for (const token of tokensByHash.values()) foregroundLoading.end(token);
      tokensByHash.clear();
      for (const query of cache.getAll()) syncQuery(query as unknown as QueryLike);
    };
    dropStaleQueryTokens();
    for (const query of cache.getAll()) syncQuery(query as unknown as QueryLike);
    const unsubscribe = cache.subscribe((event) => {
      dropStaleQueryTokens();
      const query = (event as { query?: QueryLike }).query;
      if (query) syncQuery(query);
    });
    const unsubLoading = foregroundLoading.subscribe(dropStaleQueryTokens);
    return () => {
      unsubscribe();
      unsubLoading();
      for (const token of tokensByHash.values()) foregroundLoading.end(token);
      tokensByHash.clear();
    };
  }, [queryClient]);

  useEffect(() => {
    foregroundLoading.beginSession();
    const token = foregroundLoading.beginWork("navigation", "Navigation");
    navToken.current = token;
    let released = false;

    const releaseNav = () => {
      if (released) return;
      released = true;
      if (navToken.current === token) {
        foregroundLoading.end(token);
        navToken.current = null;
      }
    };

    const tryHandoffToQueries = () => {
      if (tokensByHash.size > 0) releaseNav();
    };

    const unsub = foregroundLoading.subscribe(tryHandoffToQueries);
    tryHandoffToQueries();

    let innerRaf = 0;
    const outerRaf = window.requestAnimationFrame(() => {
      innerRaf = window.requestAnimationFrame(() => {
        // After the destination commit, either queries have registered or the
        // page has no foreground fetches. Release navigation in both cases so
        // the session cannot stick on an idle page.
        releaseNav();
      });
    });

    return () => {
      window.cancelAnimationFrame(outerRaf);
      window.cancelAnimationFrame(innerRaf);
      unsub();
      releaseNav();
    };
  }, [location]);
}
